import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./UserTransactionsTable.css";

const PAGE_SIZE = 5;

const formatValue = (value, isPercentage = false, currency = "", withTilde = false) => {
  if (value === null || value === undefined) return "-";
  const num = Number(value);
  if (isNaN(num)) return "-";

  if (isPercentage) {
    const percentageVal = num <= 1 && num > 0 ? num * 100 : num;
    const roundedInt = Math.round(percentageVal);
    const prefix = withTilde ? "~" : "";
    return `${prefix}${roundedInt}%`;
  }

  return `${num.toFixed(2)}${currency ? ` ${currency}` : ""}`;
};

const UserTransactionsTable = ({ transactions = [] }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [horizontalScrollWidth, setHorizontalScrollWidth] = useState(0);
  const [horizontalViewportWidth, setHorizontalViewportWidth] = useState(0);
  const [horizontalScrollOffset, setHorizontalScrollOffset] = useState(0);

  const containerRef = useRef(null);
  const scrollbarRef = useRef(null);
  const scrollDragRef = useRef(null);

  const totalPages = Math.ceil(transactions.length / PAGE_SIZE) || 1;
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const currentTransactions = transactions.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    const container = containerRef.current;
    const table = container?.querySelector(".user-transactions-table");
    if (!container || !table) return undefined;

    const updateScrollbar = () => {
      const contentWidth = table.scrollWidth;
      setHorizontalViewportWidth(container.clientWidth);
      setHorizontalScrollWidth(contentWidth > container.clientWidth + 1 ? contentWidth : 0);
      setHorizontalScrollOffset(container.scrollLeft);
    };

    updateScrollbar();
    const resizeObserver = new ResizeObserver(updateScrollbar);
    resizeObserver.observe(container);
    resizeObserver.observe(table);
    window.addEventListener("resize", updateScrollbar);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollbar);
    };
  }, [currentTransactions]);

  if (!transactions || transactions.length === 0) {
    return (
      <div className="user-transactions-empty">
        {t("affiliation.noTransactions", "No transactions found for this user.")}
      </div>
    );
  }

  const updateTableScroll = (scrollLeft) => {
    if (!containerRef.current) return;
    const maxScroll = Math.max(0, containerRef.current.scrollWidth - containerRef.current.clientWidth);
    const nextScrollLeft = Math.max(0, Math.min(maxScroll, scrollLeft));
    containerRef.current.scrollLeft = nextScrollLeft;
    setHorizontalScrollOffset(nextScrollLeft);
  };

  const handlePointerDown = (event) => {
    if (!scrollbarRef.current || !containerRef.current) return;
    event.preventDefault();

    const track = scrollbarRef.current;
    const tableContainer = containerRef.current;
    const trackRect = track.getBoundingClientRect();
    const maxScroll = Math.max(0, horizontalScrollWidth - tableContainer.clientWidth);
    const thumbWidth = Math.min(
      track.clientWidth,
      Math.max(32, (tableContainer.clientWidth / horizontalScrollWidth) * track.clientWidth),
    );
    const maxThumbOffset = Math.max(0, track.clientWidth - thumbWidth);
    const thumbLeft = maxScroll ? (horizontalScrollOffset / maxScroll) * maxThumbOffset : 0;
    const pointerPosition = event.clientX - trackRect.left;
    const startedOnThumb = pointerPosition >= thumbLeft && pointerPosition <= thumbLeft + thumbWidth;
    const newScroll = startedOnThumb
      ? horizontalScrollOffset
      : maxThumbOffset > 0
        ? Math.max(0, Math.min(maxScroll, ((pointerPosition - thumbWidth / 2) / maxThumbOffset) * maxScroll))
        : 0;

    if (!startedOnThumb) updateTableScroll(newScroll);
    scrollDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScroll: newScroll,
      maxScroll,
      maxThumbOffset,
    };
    track.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = scrollDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !drag.maxThumbOffset) return;
    const delta = event.clientX - drag.startX;
    updateTableScroll(drag.startScroll + (delta / drag.maxThumbOffset) * drag.maxScroll);
  };

  const handlePointerUp = (event) => {
    if (scrollDragRef.current?.pointerId !== event.pointerId) return;
    scrollDragRef.current = null;
    if (scrollbarRef.current?.hasPointerCapture(event.pointerId)) {
      scrollbarRef.current.releasePointerCapture(event.pointerId);
    }
  };

  const horizontalScrollMax = Math.max(0, horizontalScrollWidth - (containerRef.current?.clientWidth || 0));
  const horizontalThumbWidth = horizontalScrollWidth
    ? Math.min(
        horizontalViewportWidth,
        Math.max(32, (horizontalViewportWidth / horizontalScrollWidth) * horizontalViewportWidth),
      )
    : 0;
  const horizontalThumbLeft = horizontalScrollMax
    ? (horizontalScrollOffset / horizontalScrollMax) * (horizontalViewportWidth - horizontalThumbWidth)
    : 0;

  return (
    <div className="user-transactions-wrapper">
      <div
        className="user-transactions-container"
        ref={containerRef}
        onScroll={(event) => setHorizontalScrollOffset(event.currentTarget.scrollLeft)}
      >
        <table className="user-transactions-table">
          <thead>
            <tr>
              <th>{t("affiliation.esimIccid", "eSIM ICCID")}</th>
              <th>{t("affiliation.gross", "Gross")}</th>
              <th>{t("affiliation.vat", "VAT")}</th>
              <th>{t("affiliation.net", "Net")}</th>
              <th>{t("affiliation.provision", "Commission")}</th>
              <th>{t("affiliation.percent", "Level")}</th>
            </tr>
          </thead>
          <tbody>
            {currentTransactions.map((tx, index) => {
              const currency = tx.origin_provision_currency || "EUR";
              const realProvisionFormatted =
                tx.real_provision !== null && tx.real_provision !== undefined
                  ? `+${Number(tx.real_provision).toFixed(2)} ${currency}`
                  : "-";

              return (
                <tr key={tx.esim_iccid || index}>
                  <td className="iccid-cell">{tx.esim_iccid || "-"}</td>
                  <td>{formatValue(tx.total_amount_eur, false, currency)}</td>
                  <td>{formatValue(tx.vat_applied, true, "", false)}</td>
                  <td>{formatValue(tx.real_total_amount, false, currency)}</td>
                  <td className="provision-cell">{realProvisionFormatted}</td>
                  <td>{formatValue(tx.margin, true, "", true)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {horizontalScrollWidth > 0 && (
        <div
          className="user-tx-horizontal-scrollbar"
          ref={scrollbarRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          tabIndex={0}
          role="scrollbar"
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={horizontalScrollMax}
          aria-valuenow={horizontalScrollOffset}
        >
          <div
            className="user-tx-horizontal-scrollbar__thumb"
            style={{
              width: `${horizontalThumbWidth}px`,
              left: `${horizontalThumbLeft}px`,
            }}
          />
        </div>
      )}

      {totalPages > 1 && (
        <div className="user-transactions-pagination">
          <button
            type="button"
            className="user-tx-page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          >
            {t("common.prev", "Prev")}
          </button>
          <span className="user-tx-page-info">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            className="user-tx-page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          >
            {t("common.next", "Next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserTransactionsTable;
